import { styled } from "@linaria/react"
import { useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input } from "@/components/Input/Input.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"

import type { ChangeEvent } from "react"

export interface AddMCPServerDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onAddServer: (name: string, url: string) => void
}

export const AddMCPServerDialog = ({ isOpen, onOpenChange, onAddServer }: AddMCPServerDialogProps) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
      trigger={null}
      title="Connect New Remote MCP Server"
      description="The MCP server will provide additional resources (tools, prompts, etc.) to the agent to use when working on your ideas."
    >
      <AddMCPServerDialogContent onOpenChange={onOpenChange} onAddServer={onAddServer} />
    </Dialog>
  )
}

const AddMCPServerDialogContent = ({ onOpenChange, onAddServer }: {
  onOpenChange: (open: boolean) => void
  onAddServer: (name: string, url: string) => void
}) => {
  const [name, setName] = useState("Linear")
  const [nameError, setNameError] = useState<string | null>(null)

  const [url, setUrl] = useState("https://mcp.linear.app/sse")
  const [urlError, setUrlError] = useState<string | null>(null)

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
    setNameError(null)
  }

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value)
    setUrlError(null)
  }

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError("Name is required")
      return
    }

    if (!url.trim()) {
      setUrlError("URL is required")
      return
    }

    onAddServer(name.trim(), url.trim())

    setName("")
    setUrl("")
    setNameError(null)
    setUrlError(null)

    onOpenChange(false)
  }

  return (
    <Vertical gap="16px" width="100%">
      <FormField>
        <label htmlFor="mcp-server-name">Name</label>
        <Input
          id="mcp-server-name"
          value={name}
          onChange={handleNameChange}
          placeholder="Enter server name"
          autoFocus
        />
        {nameError && <ErrorText>{nameError}</ErrorText>}
      </FormField>

      <FormField>
        <label htmlFor="mcp-server-url">Server URL</label>
        <Input
          id="mcp-server-url"
          value={url}
          onChange={handleUrlChange}
          placeholder="Enter remote MCP server URL"
          type="url"
        />
        {urlError && <ErrorText>{urlError}</ErrorText>}
      </FormField>

      <Horizontal justifyContent="flex-end" gap="8px">
        <Button kind="alternative" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Tooltip content="WIP: There is no way to undo this!">
          <Button kind="danger" onClick={handleSubmit}>
            Connect
          </Button>
        </Tooltip>
      </Horizontal>
    </Vertical>
  )
}

const FormField = styled(Vertical)`
  gap: 8px;

  label {
    font-weight: 500;
  }
`

const ErrorText = styled.span`
  color: var(--error-color);
  font-size: 0.875rem;
`
